import React, {useEffect, useRef, useState} from "react";
import {Linking, StyleSheet, View, Image as RNImage, Animated, Easing, Platform} from "react-native";
import {Button, Image, Text, VStack} from "native-base";
import apps from "./lib/apps";
import {isShopOpenNow, isHolidayToday, pickDayHours, closedReason, formatTime12, nextOpening, NextOpen} from "./lib/hours";
import {PALETTE} from "./lib/palette";

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
// 다음 오픈을 자연어로: "today at 11:00 AM" / "tomorrow (Sat) at 11:00 AM" / "Sat at 11:00 AM"
function nextOpenPhrase(next: NextOpen, now: Date): string {
    const time = formatTime12(next.open);
    const d0 = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const d1 = new Date(next.date.getFullYear(), next.date.getMonth(), next.date.getDate());
    const diff = Math.round((d1.getTime() - d0.getTime()) / 86400000);
    const wd = WEEKDAYS[next.date.getDay()];
    if (diff <= 0) return `today at ${time}`;
    if (diff === 1) return `tomorrow (${wd}) at ${time}`;
    return `${wd} at ${time}`;
}
import {ShopTblData, UserData, YourOrder} from "./lib/model";
import * as remote from "./lib/remote";
import {isReloadOrRestore, markPageShow} from "./lib/nav";
import {shopLogoUri} from "./lib/utils";
import {hasCompletedOrder, clearLastOrder} from "./lib/lastOrder";
import {useRecoilState, useSetRecoilState} from "recoil";
import {orderState, sessionActiveState} from "./state/orderState";
import {v4 as uuidv4} from "uuid";
import safeStorage from "./lib/storage";
import {armBackGuard} from "./lib/backGuard";
import Farewell from "./Farewell";
import SessionExpired from "./SessionExpired";
import Shimmer from "./Shimmer";

// RFC4122 v4 UUID via Math.random() — not cryptographically secure, acceptable for a non-sensitive session identifier.
// Used as fallback when crypto.getRandomValues() is unavailable (iOS < 13).
function fallbackUuid(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

/**
 * Splash screen
 * 1.5초 유지후 url에서 shopId와 tableNo를 추출해서 저장하고 메인 스택으로 넘어간다.
 * UR: /:shopId/:tableNo
 */
function Splash({navigation}) {
    const [yourOrder, setYourOrder] = useRecoilState(orderState);
    const setSessionActive = useSetRecoilState(sessionActiveState);
    const [start, setStart] = useState(false);

    const [errorMessage, setErrorMessage] = useState("");        // 에러메시지
    const [errorVisible, setErrorVisible] = useState(false);        // 에러 모달
    const [showBrowsingMenuButton, setShowBrowsingMenuButton] = useState(false);
    const [logoShopId, setLogoShopId] = useState("");   // 매장별 로고용 shopId
    const [logoFailed, setLogoFailed] = useState(false); // S3 로고 404 → 번들 로고 폴백
    const logoBust = useRef(Date.now()).current;   // 스플래시 마운트 시각(스캔마다 새 값 → 로고 캐시버스트)
    const timerRef = useRef(null); // Store the interval reference
    const hasStopped = useRef(false); // Prevent multiple interval stops

    // 랜딩(back-guard): 자동 진입하지 않고 사용자 탭을 강제한다. 탭(제스처)에서 동기 pushState로
    // non-skippable 히스토리를 만들어야 삼성 카메라-탭에서 back이 탭을 안 닫고 popstate로 잡힌다.
    const [showStart, setShowStart] = useState(false);           // "Tap to Start" 버튼 표시
    const [farewell, setFarewell] = useState(false);             // Leave로 나갈 때만(_bye 마커) "See you next time" 화면
    const [sessionExpired, setSessionExpired] = useState(false); // 마커 없는 만료 → 카메라 뷰파인더 화면
    const [preparing, setPreparing] = useState(false);           // 주문 가능 확정 후 0.5초 스피너 → preparing(문구) 1초
    const startingRef = useRef(false);                           // 탭 연타로 히스토리 여러 개 밀리는 것 방지
    const prepareTimer = useRef(null);                           // preparing → Tap to Start 전환 타이머
    const breathe = useRef(new Animated.Value(0)).current;       // 랜딩 로고 은은한 breathing

    // 닫힘/에러 화면의 상태 칩(라벨 + 톤). tone: amber=닫힘/휴일, blue=일시 휴게, gray=만료/오류.
    const [chipText, setChipText] = useState("");
    const [chipTone, setChipTone] = useState<"amber" | "blue" | "gray">("gray");

    /**
     * uuid를 생성하고 localStorage에 저장한다. 기존에 저장된 localStorage가 없으면 새로 생성한다.
     */
    function getUuid() {
        let uuid = safeStorage.getItem("uuid");
        // null == undefined → true: covers both "key not found" and "storage blocked" (iOS private mode).
        // In the blocked case, uuid is regenerated every session — acceptable degraded behaviour.
        if (uuid == undefined) {
            try {
                uuid = uuidv4();
            } catch (e) {
                console.warn('[uuid] crypto fallback activated:', e);
                uuid = fallbackUuid();
            }
            safeStorage.setItem("uuid", uuid);
        }
        return uuid;
    }

    function stopTimerOnce() {
        if (!hasStopped.current) {
            clearInterval(timerRef.current);
            hasStopped.current = true;
        }
    }

    // 닫힘/에러 화면을 띄운다: 상태 칩(라벨+톤) + 가운데 메시지 (+ 필요 시 View Menu Only).
    // 비-orderable 결과는 0.7초 스피너 후 표시 → 빠른 응답 시 깜빡이며 툭 바뀌는 것 방지(부드러운 전환).
    function showNotice(msg: string, chip: string, tone: "amber" | "blue" | "gray", withMenu: boolean) {
        stopTimerOnce();
        prepareTimer.current = setTimeout(() => {
            setErrorMessage(msg);
            setChipText(chip);
            setChipTone(tone);
            setShowBrowsingMenuButton(withMenu);
            setErrorVisible(true);
        }, 700);
    }

    function buildMenuUrl(currentUrl) {
        const url = new URL(currentUrl);
        const path = url.search.replace("?", "").split("/");

        if (path.length >= 2) {
            path[1] = "menu";
        }

        url.search = "?" + path.join("/");
        return url.toString();
    }

    // directEnter=false: 검증 성공 시 자동 진입 대신 "Tap to Start" 표시(랜딩).
    // directEnter=true: 이미 사용자 제스처(View Menu Only)로 pushState/무장을 끝냈으니 바로 앱 진입.
    async function getUrl(overrideUrl = null, directEnter = false) {

        if (hasStopped.current) return; // Stop execution if the process is already stopped

        //const url = new URL(await Linking.getInitialURL());
        const rawUrl = overrideUrl || await Linking.getInitialURL();
        const url = new URL(rawUrl);

        if (!url) return;

        const path = url.search.replace("?", "").split("/");

        //console.log(path);
        if (path.length < 5) {
            // Leave로 나가면 `?shopId/_bye`로 replace됨 → 마커가 있으면 매장 로고 + "See you next time" farewell.
            // URL 직접 입력·손상된 QR 등 마커 없는 만료는 손 흔듦 없이 평범한 Session Expired.
            // 이 둘은 스피너·의도적 지연 없이 즉시 표시(전용 화면이라 자체 등장 연출이 있음).
            if (path[1] === "_bye") {
                stopTimerOnce();
                setFarewell(true);
                return;
            }
            // 마커 없는 만료 → 카메라 뷰파인더 디자인(SessionExpired 컴포넌트)
            stopTimerOnce();
            setSessionExpired(true);
            return;
        }

        let uuid = getUuid();

        apps.shopId = path[0];
        apps.replaceShopId();

        apps.tableNo = path[1];
        apps.txId = Number(path[2]);
        apps.signature = path[3];
        apps.profile = path[4];
        if (path[5]) apps.ipLast = path[5];

        // 인자 갯수는 맞췄지만(예: `shopid//txId/sig/profile`) 필수 조각이 비었거나 공백뿐이면 잘못된 접근이다.
        // 서버 호출 없이 즉시 일반 Invalid QR 안내(재시도 중단) — 서버 404에 기대지 않고, 공백 우회도 여기서 막는다.
        // txId(path[2])는 미연동 QR에서 빈 값(→0)이 정상이라 검사 대상에서 제외한다.
        if (![apps.shopId, apps.tableNo, apps.signature, apps.profile].every(v => (v ?? "").trim().length > 0)) {
            showNotice("You are currently using an invalid QR Code. Please contact a staff member for assistance.", "Invalid QR", "gray", false);
            return;
        }

        //console.log("shopId: " + apps.shopId);
        //console.log("tableNo: " + apps.tableNo);
        //console.log("TX Id: " + apps.txId);
        //console.log("Signature: " + apps.signature);
        //console.log("Profile: " + apps.profile);
        //console.log("ipLast: " + apps.ipLast);

        let shop: ShopTblData;
        let user: UserData;
        try {
            shop = await remote.getShop(apps.shopId, apps.tableNo);
            user = await remote.findUser(uuid);
        } catch (e) {
            // 없는 shopid 등 잘못된 접근이면 서버가 어떤 상태코드로든 "응답"한다(PikashopException→500, 라우팅 실패→404 등).
            // 서버가 응답을 준 경우는 재시도해도 동일하므로 일반 Invalid QR 안내(매장 존재 여부 등 힌트 노출 금지) 후 중단한다.
            // 응답 자체가 없는 경우(네트워크 단절·타임아웃·서버 기동 전)만 일시적 오류로 보고 1.5초 인터벌 재시도에 맡긴다.
            // NOTE: 서버 TYPE:PIKASHOP_ERROR 헤더는 CORS(exposedHeaders 미설정)로 브라우저에서 읽을 수 없어 헤더/상태코드에 의존하지 않는다.
            const hasResponse = !!(e as any)?.response;
            if (hasResponse) {
                showNotice("You are currently using an invalid QR Code. Please contact a staff member for assistance.", "Invalid QR", "gray", false);
            }
            return;
        }
        //console.log("shop: " + shop);
        apps.currentShop = shop;
        apps.qrType = shop.qrType;

        //console.log(shop);

        //console.log("userId: " + user.id);
        apps.user = user;
        apps.name = safeStorage.getItem("name") || "";
        apps.mobile = safeStorage.getItem("mobile") || "";

        if (apps.signature != shop.signature) {
            const message = "You are currently using an invalid QR Code. Please contact a staff member for assistance.";
            showNotice(message, "Invalid QR", "gray", false);
            return;
        }

        if (shop.status !== "ACTIVE") {
            let message = "QR System is currently Offline";
            let chip = "Offline";
            let tone: "amber" | "blue" | "gray" = "gray";
            if (shop.status == "QROFF") {
                /* 매장은 영업중이지만, QR만 OFF하고 싶을때 사용함 */
                message = "QR System is currently Offline. Please use an alternative system.";
            }
            if (shop.status == "INACTIVE") {
                /* 매장이 공휴일 혹은 사정상 임시로 하루 혹은 몇일동안 문을 닫았을때 */
                message = shop.name + " is currently closed.";
                chip = "Closed"; tone = "amber";
            }
            showNotice(message, chip, tone, false);
            return;
        }


        const now = new Date();
        // tableNo가 "menu"인지 대소문자 구분 없이 확인
        const isMenuMode = apps.tableNo && apps.tableNo.toLowerCase() === "menu";

        // 재주문 방지 가드: 완료된 주문의 reload/bfcache 복원이면 주문가능 세션을 부활시키지 않는다.
        // fresh 스캔(reload 아님)이면 통과 → 정상 주문. (bfcache/reload 감지는 캐주얼 방지용, URL 변경과 이중.)
        if (!isMenuMode && isReloadOrRestore() && hasCompletedOrder(getUuid())) {
            showNotice("You've already placed your order. To order again, please scan the table QR code.", "Order placed", "gray", false);
            return;
        }

        // 영업 여부 판정: 자정 넘김(wrap) 세션의 다음날 새벽 tail까지 오케스트레이터가 단일 판정한다.
        // 전날 밤 영업이 오늘 새벽까지 이어지면 오늘 요일/휴일과 무관하게 열림(#13 정책: tail은 전날을 따름).
        // menu 모드는 영업/휴일 무관하게 통과.
        if (!isMenuMode && !isShopOpenNow(shop, now, shop.holidays)) {
            const day = pickDayHours(shop, now.getDay());
            const nowMin = now.getHours() * 60 + now.getMinutes();
            const todayHoliday = isHolidayToday(shop.holidays, now.getFullYear(), now.getMonth() + 1, now.getDate());
            let msg: string;
            let chip = "Closed now";
            let tone: "amber" | "blue" | "gray" = "amber";
            if (todayHoliday) {
                // 오늘 휴일이고 전날 tail도 아님 → 휴일 안내
                const next = nextOpening(shop, now, shop.holidays);
                const tail = next ? ` We reopen ${nextOpenPhrase(next, now)}.` : ` We hope to serve you again soon.`;
                msg = `${shop.name} is closed for a holiday today.${tail}`;
                chip = "Closed today";
            } else {
                // 닫힘 사유 우선순위: off(요일휴무) → outside(영업시간외) → break(윈도우 내 휴게).
                // off인 날엔 break 시각이 남아있어도 'break'로 오표시하지 않는다.
                const reason = closedReason(day, nowMin);
                if (reason === "break") {
                    // 영업 윈도우 내 휴게: 구간 + 재개 시각 안내
                    msg = `${shop.name} is on a break from ${formatTime12(day.breakStart)} to ${formatTime12(day.breakEnd)}. Ordering reopens at ${formatTime12(day.breakEnd)}.`;
                    chip = "On a break"; tone = "blue";
                } else {
                    // off 또는 영업시간 밖: 다음 오픈 시각을 안내(오늘 오픈 전이면 오늘, 아니면 다음 영업일)
                    const next = nextOpening(shop, now, shop.holidays);
                    msg = next
                        ? `${shop.name} is closed now — opens ${nextOpenPhrase(next, now)}.`
                        : `${shop.name} is currently closed. Please visit during our business hours.`;
                }
            }
            showNotice(msg, chip, tone, true);
            return;
        }

        setShowBrowsingMenuButton(false);
        // fresh 스캔으로 새 주문 세션 시작 → 지난 완료 마커 제거(이번 세션 '주문 전 reload' 오탐 방지).
        if (!isMenuMode) clearLastOrder(getUuid());
        setYourOrder(new YourOrder(apps.shopId, apps.qrType, apps.tableNo, apps.txId));
        setSessionActive(true);   // QR 세션 활성(레거시 신호, 무해)

        if (!directEnter) {
            // 랜딩: 자동 진입하지 않고 "Tap to Start"를 띄운다. 실제 진입은 사용자 탭(proceedToApp)에서
            // 동기 pushState + 무장 후 이뤄진다(back-guard 원리).
            // 주문 가능 확정: 0.5초 스피너(문구 없음) → 1초 "Preparing your table…"(스피너) → Tap to Start.
            stopTimerOnce();
            prepareTimer.current = setTimeout(() => {
                setPreparing(true);
                prepareTimer.current = setTimeout(() => setShowStart(true), 1000);
            }, 500);
            return;
        }

        // View Menu Only 등 이미 제스처로 pushState/무장을 끝낸 경우 → 바로 진입.
        setStart(true);
        clearInterval(timerRef.current); // Stop the interval after success
    };

    // "View Menu Only"(휴일/break) → 사용자 제스처. gotcha#1: pushState를 await 이전에 "동기"로 실행해야
    // user activation이 유지돼 non-skippable 히스토리가 생긴다. 웹에선 window.location.href가 동기라 이걸 사용.
    function openBrowsingMenu() {
        if (startingRef.current) return;
        startingRef.current = true;
        if (typeof window === "undefined") return;

        const menuUrl = buildMenuUrl(window.location.href);   // 동기
        window.history.pushState({psGuard: 1}, "", menuUrl);  // 동기 · 제스처 → non-skippable + 무장 준비
        armBackGuard();

        hasStopped.current = false;
        setErrorVisible(false);
        setShowBrowsingMenuButton(false);
        getUrl(menuUrl, true);   // menu 모드로 검증 후 바로 진입(directEnter)
    };

    // "Tap to Start" → 사용자 제스처. gotcha#1: 동기 pushState(await 이전) → non-skippable 히스토리.
    // 이후 back은 App 레벨 BackGuard가 popstate로 잡아 Leave/Stay 모달을 띄운다.
    function proceedToApp() {
        if (startingRef.current) return;
        startingRef.current = true;
        if (typeof window !== "undefined") window.history.pushState({psGuard: 1}, "");
        armBackGuard();
        setStart(true);   // → useEffect navigation.replace("AppStack")
    }

    useEffect(() => {
        getUrl();   // 마운트 즉시 검증(고정 1.5초 대기 제거) → 스피너는 네트워크 대기 중에만
        timerRef.current = setInterval(async () => {
            await getUrl();
        }, 1500);   // 느린 네트워크/미준비 대비 재시도

        return function cleanup() {
            clearInterval(timerRef.current);
            if (prepareTimer.current) clearTimeout(prepareTimer.current);
            setStart(false);
        };
    }, [])

    // bfcache 복원 감지: pageshow.persisted=true면 reload/restore로 표시(재주문 방지 가드용)
    useEffect(() => {
        if (typeof window === "undefined" || !window.addEventListener) return;
        const h = (e: any) => markPageShow(!!e.persisted);
        window.addEventListener("pageshow", h);
        return () => window.removeEventListener("pageshow", h);
    }, [])

    // 마운트 즉시 URL에서 shopId를 파싱해 매장별 로고를 스플래시 뜨자마자 로드(1.5s 폴링을 기다리지 않음).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const raw = await Linking.getInitialURL();
                if (!raw || cancelled) return;
                const p = new URL(raw).search.replace("?", "").split("/");
                if (p[0]) {
                    apps.shopId = p[0];
                    apps.replaceShopId();   // koisushi→onarollch 등 상호 변경 반영(getUrl과 동일)
                    if (!cancelled) { setLogoShopId(apps.shopId); setLogoFailed(false); }
                }
            } catch { /* URL 파싱 실패 시 번들 로고 유지 */ }
        })();
        return () => { cancelled = true; };
    }, [])

    useEffect(() => {
        if (start) {
            navigation.replace("AppStack");
        }
    }, [start, navigation])

    // 랜딩("Tap to Start")에서만 로고를 은은하게 breathing(느린 4초 루프). prefers-reduced-motion 존중.
    useEffect(() => {
        if (!showStart) { breathe.stopAnimation(); breathe.setValue(0); return; }
        const reduce = Platform.OS === "web" && typeof window !== "undefined" && !!window.matchMedia
            && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        if (reduce) return;
        const loop = Animated.loop(Animated.sequence([
            Animated.timing(breathe, {toValue: 1, duration: 2000, easing: Easing.inOut(Easing.quad), useNativeDriver: false}),
            Animated.timing(breathe, {toValue: 0, duration: 2000, easing: Easing.inOut(Easing.quad), useNativeDriver: false}),
        ]));
        loop.start();
        return () => loop.stop();
    }, [showStart])

    const accent = apps.getAccent();
    const onAccent = apps.onAccent();

    // 매장별 로고: S3 shoplogo/{shopid}.png → 로드 실패(onError)면 번들 로고로 폴백.
    // 순수 RN Image 사용: native-base Image는 내부 renderedSource 상태 때문에 웹에서 onError 후
    // source 교체가 리셋되지 않아 폴백이 안 먹었음(RNW의 <img> onerror는 안정적으로 동작).
    const usingShopLogo = !!(logoShopId && !logoFailed);
    const logoSource = usingShopLogo
        ? shopLogoUri(logoShopId, logoBust)   // 캐시버스트: 마운트 시각(스플래시는 설정 로드 전 렌더라 logoImageVer 못 씀)
        : require("../assets/splash_logo.png");   // 매장 로고 없음(예: Session Expired) → 번들 pikashop 로고 폴백

    // 테이블 줄: online/menu(대소문자 무관)는 프리픽스 없이 그대로, 그 외는 "Table No. N".
    const tno = (apps.tableNo || "").trim();
    const tnoLower = tno.toLowerCase();
    const tableText = tnoLower === "online" ? "Online"
        : tnoLower === "menu" ? "Menu"
        : (tno ? "Table No. " + tno : "");

    // 상태 칩 톤(닫힘=amber, 휴게=blue, 만료/오류=gray).
    const chipBox = chipTone === "amber" ? styles.chipAmber : chipTone === "blue" ? styles.chipBlue : styles.chipGray;
    const chipTxt = chipTone === "amber" ? styles.chipAmberTxt : chipTone === "blue" ? styles.chipBlueTxt : styles.chipGrayTxt;
    const chipDotColor = chipTone === "amber" ? "#d97706" : chipTone === "blue" ? "#3b82f6" : "#9aa1ad";

    // 랜딩 로고 breathing 트랜스폼(showStart 아니면 breathe=0 → 정지 상태와 동일).
    const breatheStyle = {
        transform: [
            {translateY: breathe.interpolate({inputRange: [0, 1], outputRange: [0, -4]})},
            {scale: breathe.interpolate({inputRange: [0, 1], outputRange: [1, 1.015]})},
        ],
    };

    return (
        <View style={styles.container}>
            <VStack style={styles.flow} alignItems="center">
                {farewell ? (
                    /* Leave로 나감 → "See you next time" 작별 애니메이션(매장 로고 + 손 흔들기) */
                    <Farewell logoSource={logoSource} accent={accent} />
                ) : sessionExpired ? (
                    /* 마커 없는 만료 → 카메라 뷰파인더(QR 재스캔 유도) */
                    <SessionExpired accent={accent} />
                ) : (
                  <>
                {/* 로고: 매장 로고는 200x200 정사각(흰 배경, 투명 PNG도 크롭 없이). 매장 로고가 없어
                    번들 pikashop 로고로 폴백될 때는(예: Session Expired) 더 작게 렌더해 과하게 커 보이지 않게 한다.
                    랜딩("Tap to Start")에선 은은하게 breathing(breatheStyle). */}
                {/* 로고 자리는 항상 200×200 슬롯으로 고정 → 폴백(번들) 로고가 130이어도 아래 칩/스피너 위치가
                    매장 로고(200) 때와 동일하게 유지된다(예: 없는 shopid의 Invalid QR 칩 위치를 서명 불일치 때와 맞춤). */}
                <View style={styles.logoSlot}>
                <Animated.Image style={[styles.logoImg, !usingShopLogo && styles.logoImgFallback, breatheStyle]} accessibilityLabel="logo"
                         source={logoSource} onError={() => setLogoFailed(true)} />
                </View>

                {errorVisible ? (
                    /* 닫힘/에러: 로고 유지 + 가운데 정렬된 상태 칩 + 메시지 (+ 필요 시 View Menu Only) */
                    <>
                        <View style={styles.centerBlock}>
                            {chipText ? (
                                <View style={[styles.chip, chipBox]}>
                                    <View style={[styles.chipDot, {backgroundColor: chipDotColor}]} />
                                    <Text style={[styles.chipTxt, chipTxt]}>{chipText}</Text>
                                </View>
                            ) : null}
                            <Text style={styles.noticeMsg}>{errorMessage}</Text>
                        </View>
                        <View style={styles.spring} />
                        {showBrowsingMenuButton ? (
                            <Button
                                w="100%"
                                maxW={420}
                                bg={accent}
                                _text={{ color: onAccent, fontWeight: '800', fontSize: 14 }}
                                borderRadius={14}
                                py={4}
                                onPress={openBrowsingMenu}
                            >
                                View Menu Only
                            </Button>
                        ) : (
                            /* 버튼 없는 상태(만료/오류)도 Closed 화면과 같은 높이에 오도록 버튼 자리만큼 확보 */
                            <View style={styles.btnReserve} />
                        )}
                    </>
                ) : showStart ? (
                    /* 랜딩: Welcome + 매장명 + 테이블 줄 + Tap to Start(제스처 pushState로 back-guard 무장) */
                    <>
                        <Text style={styles.welcome}>Welcome to</Text>
                        {apps.currentShop?.name ? (
                            <Text style={styles.shopName}>{apps.currentShop.name}</Text>
                        ) : null}
                        {tableText ? (
                            <View style={[styles.tbl, {borderColor: accent}]}>
                                <View style={[styles.tblDot, {backgroundColor: accent}]} />
                                <Text style={[styles.tblTxt, {color: accent}]}>{tableText}</Text>
                            </View>
                        ) : null}
                        <View style={styles.spring} />
                        <Button
                            w="100%"
                            maxW={420}
                            bg={accent}
                            _text={{ color: onAccent, fontWeight: '800', fontSize: 15 }}
                            borderRadius={14}
                            py={4}
                            onPress={proceedToApp}
                        >
                            {tnoLower === "menu" ? "View Menu Only" : "Tap to Start"}
                        </Button>
                    </>
                ) : (
                    /* Loading — 스피너+문구를 상단(Welcome 자리 근처)에 배치해 showStart 전환 시 위치가 튀지 않게 */
                    <>
                        <View style={styles.loadCluster}>
                            {/* 초기(매장 설정 로드 전)엔 중립 회색, currentShop 로드 후 매장 accent → 기본색 깜빡임 방지 */}
                        <Shimmer color={apps.currentShop ? accent : "#b6bcc6"} />
                            {/* 문구 자리는 항상 확보(opacity만 전환) → preparing 토글 시 스피너가 위로 튀지 않음 */}
                            <Text style={[styles.status, !preparing && {opacity: 0}]}>Preparing your table...</Text>
                        </View>
                        <View style={styles.spring} />
                        <View style={styles.btnReserve} />
                    </>
                )}
                  </>
                )}

                <View style={styles.footer}>
                    <Text style={styles.verText}>Version 2.0</Text>
                    <Image mt={1} w={200} h={15} resizeMode="contain" alt="pikashop by medianara" source={require("../assets/pikashopbymedianara.png")} />
                </View>
            </VStack>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: PALETTE.SURFACE,
    },
    flow: {
        flex: 1,
        paddingTop: 60,
        paddingBottom: 24,
        paddingHorizontal: 24,
    },
    logoSlot: {   // 로고 자리 고정(200×200): 폴백 로고가 작아도 아래 요소들의 위치가 안 바뀌게
        width: 200,
        height: 200,
        alignItems: 'center',
        justifyContent: 'center',
    },
    logoImg: {
        width: 200,
        height: 200,
        resizeMode: 'contain',
    },
    logoImgFallback: {   // 번들 pikashop 폴백 로고는 더 작게(슬롯 안에서 중앙 정렬)
        width: 130,
        height: 130,
    },
    // 랜딩
    welcome: {
        marginTop: 40,   // Welcome/칩/Spinner 상단 위치 통일
        fontSize: 11,
        letterSpacing: 1.6,
        textTransform: 'uppercase',
        color: '#9aa1ad',
        fontWeight: '700',
        textAlign: 'center',
    },
    shopName: {
        fontSize: 25,
        fontWeight: '800',
        color: PALETTE.TEXT,
        letterSpacing: -0.4,
        marginTop: 6,
        textAlign: 'center',
    },
    tbl: {
        marginTop: 14,
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        borderWidth: 1.5,
        borderRadius: 999,
        paddingVertical: 6,
        paddingHorizontal: 15,
    },
    tblDot: { width: 6, height: 6, borderRadius: 3, marginRight: 7 },
    tblTxt: { fontSize: 13, fontWeight: '700' },
    // 닫힘/에러
    centerBlock: {
        alignItems: 'center',
        paddingHorizontal: 8,
        // 로고 바로 아래(Welcome/Spinner와 같은 위치)에 상단 정렬 → 상태 전환 시 위치 안 튐.
        // 상단 정렬이라 메시지 길이와 무관하게 칩이 항상 같은 Y(marginTop)에 온다.
        marginTop: 40,
    },
    chip: {
        flexDirection: 'row',
        alignItems: 'center',
        alignSelf: 'center',
        borderWidth: 1,
        borderRadius: 999,
        paddingVertical: 7,
        paddingHorizontal: 14,
    },
    chipDot: { width: 6, height: 6, borderRadius: 3, marginRight: 7 },
    chipTxt: { fontSize: 11, fontWeight: '800', letterSpacing: 1, textTransform: 'uppercase' },
    chipAmber: { backgroundColor: '#fef4e6', borderColor: '#f4cf9a' },
    chipAmberTxt: { color: '#b45309' },
    chipBlue: { backgroundColor: '#eaf0fe', borderColor: '#bcd0f7' },
    chipBlueTxt: { color: '#1d4ed8' },
    chipGray: { backgroundColor: '#f1f3f6', borderColor: '#e2e5ea' },
    chipGrayTxt: { color: '#6b7280' },
    noticeMsg: {
        marginTop: 14,
        fontSize: 14,
        lineHeight: 22,
        color: PALETTE.MUTED,
        textAlign: 'center',
        maxWidth: 260,
    },
    status: {
        fontSize: 12,
        color: PALETTE.MUTED,
        marginTop: 10,
        textAlign: 'center',
    },
    loadCluster: {
        marginTop: 40,   // 로고 바로 아래(Welcome 자리 근처) — showStart 전환 시 위치 튐 방지
        alignItems: 'center',
    },
    spring: { flex: 1 },
    btnReserve: { height: 54 },   // View Menu Only 버튼 높이만큼(버튼 없는 화면 정렬 맞춤)
    footer: {
        marginTop: 18,
        alignItems: 'center',
    },
    verText: {
        fontSize: 12,
        fontWeight: '700',
        color: PALETTE.MUTED,
        textAlign: 'center',
    },
});

export default Splash;
